// The public site's product previews, made to work: send a message and get a reply, click round the
// dashboard, fill in the donation form, switch filters, test an automation. Everything here is
// made-up example data in the page; nothing is sent anywhere. Without this script the previews
// still show their first state.
const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
const wait = (ms: number) => new Promise(r => setTimeout(r, reduced ? 0 : ms));

type Person = { id: string; name: string; av: string; tone: string; channel: string; chip: string; stage: string; given: string; fund: string; last: string; thread: [('in' | 'out'), string][]; replies: string[] };
const PEOPLE: Person[] = [
  { id: 'amina', name: 'Amina K.', av: 'AK', tone: 'coral', channel: 'WhatsApp', chip: 'green', stage: 'Fundraiser', given: '£340 raised', fund: 'Atlas Trek 2027',
    last: 'Is the target the same for two of us?',
    thread: [['out', 'Hi Amina, your Atlas trek info pack is attached. Any questions, just reply here.'], ['in', 'Thank you. Is the target the same if two of us sign up?']],
    replies: ['That makes sense, thank you. We will both sign up tonight.', 'Perfect. Can my sister use the same link?', 'Lovely, see you at the training walk.'] },
  { id: 'yusuf', name: 'Yusuf Rahman', av: 'YR', tone: 'blue', channel: 'Email', chip: 'blue', stage: 'Donor', given: '£1,250 given', fund: 'Monthly giving',
    last: 'Can I update my monthly gift?',
    thread: [['in', 'Hi, can I move my monthly gift from £20 to £30 from next month?']],
    replies: ['Brilliant, thank you for sorting that so quickly.', 'And could I have a statement for my tax return?', 'Thanks, that all looks right.'] },
  { id: 'sarah', name: 'Sarah Patel', av: 'SP', tone: 'sun', channel: 'Email', chip: 'blue', stage: 'Lead', given: '£25 given', fund: 'Winter appeal',
    last: 'Thank you for the update!',
    thread: [['out', 'Here is our autumn update: 312 families reached so far, thanks to people like you.'], ['in', 'Thank you for the update. Lovely to see where it goes.']],
    replies: ['I would like to give monthly. Where do I start?', 'Thank you, I will share it with my friends.', 'Happy to help with the next appeal.'] },
  { id: 'omar', name: 'Omar Siddiqui', av: 'OS', tone: 'green', channel: 'SMS', chip: 'sun', stage: 'Donor', given: '£120 given', fund: 'Zakat',
    last: 'Receipt please',
    thread: [['in', 'Hi, could I get a receipt for my Zakat gift please?']],
    replies: ['Got it, thank you.', 'Thanks. Is Gift Aid on that too?', 'Perfect, have a good day.'] },
];
const person = (id: string) => PEOPLE.find(p => p.id === id) ?? PEOPLE[0]!;

// ---------- Conversation threads: type, send, get a believable reply ----------
function bubble(thread: HTMLElement, dir: 'in' | 'out', text: string) {
  const b = document.createElement('div');
  b.className = `bub ${dir}`;
  b.textContent = text;
  thread.append(b);
  thread.scrollTop = thread.scrollHeight;
  return b;
}
function chat(root: HTMLElement) {
  const thread = root.querySelector<HTMLElement>('[data-thread]')!;
  const form = root.querySelector<HTMLFormElement>('[data-compose]')!;
  const input = form.querySelector('input')!;
  let who = person(root.dataset.chat!), turn = 0, busy = false;
  const send = async (text: string) => {
    if (!text.trim() || busy) return;
    busy = true;
    bubble(thread, 'out', text.trim());
    input.value = '';
    await wait(500);
    const dots = bubble(thread, 'in', '');
    dots.classList.add('typing');
    dots.setAttribute('aria-label', `${who.name} is typing`);
    dots.innerHTML = '<i></i><i></i><i></i>';
    await wait(1300);
    dots.remove();
    bubble(thread, 'in', who.replies[turn++ % who.replies.length]!);
    busy = false;
  };
  form.addEventListener('submit', e => { e.preventDefault(); void send(input.value); });
  root.querySelectorAll<HTMLButtonElement>('[data-quick] button').forEach(b => b.addEventListener('click', () => void send(b.textContent!)));
  // The features page lists conversations beside the thread: pick one to open it.
  const list = [...root.querySelectorAll<HTMLButtonElement>('[data-person]')];
  list.forEach(b => b.addEventListener('click', () => {
    list.forEach(x => { const on = x === b; x.classList.toggle('on', on); x.setAttribute('aria-pressed', String(on)); });
    who = person(b.dataset.person!); turn = 0;
    thread.replaceChildren();
    who.thread.forEach(([d, t]) => bubble(thread, d, t));
    input.placeholder = `Reply on ${who.channel}…`;
  }));
}
document.querySelectorAll<HTMLElement>('[data-chat]').forEach(chat);

// ---------- Homepage dashboard: every part of the shot does something ----------
const METRICS: Record<string, { title: string; bars: number[] }> = {
  gifts: { title: 'Donations, last 8 weeks', bars: [40, 55, 48, 70, 62, 80, 74, 95] },
  supporters: { title: 'New supporters, last 8 weeks', bars: [30, 42, 38, 52, 60, 55, 72, 84] },
  giftaid: { title: 'Gift Aid ready to claim, last 8 weeks', bars: [22, 30, 26, 41, 38, 52, 49, 66] },
};
const top = (title: string, sub: string, action = '') =>
  `<div class="shot-top"><div><b>${title}</b><small>${sub}</small></div>${action}</div>`;
const whoRow = (p: Person, extra = '') =>
  `<button type="button" class="who" data-open="${p.id}"><span class="av ${p.tone}">${p.av}</span><span><b>${p.name}</b><small>${extra || p.last}</small></span><span class="chiplet ${p.chip}">${p.channel}</span></button>`;

const VIEWS: Record<string, (open?: string) => string> = {
  inbox: open => {
    const p = person(open ?? 'amina');
    return top('Inbox', '4 conversations need a reply') + `<div class="shot-split">
      <div class="shot-panel shot-list">${PEOPLE.map(x => whoRow(x)).join('')}</div>
      <div class="shot-panel" data-chat="${p.id}"><b>${p.name} · ${p.channel}</b>
        <div class="p-thread" data-thread aria-live="polite" style="min-height:9rem;background:none;padding:0"></div>
        <form class="p-compose" data-compose style="padding:0;border:0"><label class="visually-hidden" for="shot-reply">Write a reply</label><input id="shot-reply" type="text" autocomplete="off" maxlength="200" placeholder="Reply on ${p.channel}…"><button>Send</button></form>
      </div></div>`;
  },
  supporters: open => {
    const p = person(open ?? 'yusuf');
    return top('Supporters', '1,284 people · 3 filters saved', '<button type="button" data-add>+ Add supporter</button>') + `<div class="shot-split">
      <div class="shot-panel shot-list">${PEOPLE.map(x => whoRow(x, `${x.stage} · ${x.given}`).replace('class="who"', `class="who${x.id === p.id ? ' on' : ''}" data-profile="${x.id}"`).replace(` data-open="${x.id}"`, '')).join('')}</div>
      <div class="shot-panel"><div class="who"><span class="av ${p.tone}" style="width:44px;height:44px;font-size:.875rem">${p.av}</span><span><b style="font-size:1rem">${p.name}</b><small>${p.stage} · prefers ${p.channel}</small></span></div>
        <div class="shot-stats"><div class="shot-stat"><small>Giving</small><b>${p.given.split(' ')[0]}</b><i>${p.fund}</i></div><div class="shot-stat"><small>Gift Aid</small><b>✓</b><i>Declaration on file</i></div></div>
        <div class="shot-row"><small>Last message: “${p.last}”</small><button type="button" class="chiplet blue" data-open="${p.id}">Message</button></div></div></div>`;
  },
  gifts: () => top('Gifts', 'This month', '<button type="button" data-build-claim>Build Gift Aid claim</button>') + `
    <div class="shot-stats"><div class="shot-stat"><small>This month</small><b>£4,820</b><i>▲ 12% on last month</i></div><div class="shot-stat"><small>Monthly givers</small><b>112</b><i>▲ 6 this month</i></div><div class="shot-stat"><small>Gift Aid to claim</small><b data-claim-total>£1,205</b><i class="blue">41 gifts</i></div></div>
    <div class="shot-panel shot-list"><b>Latest gifts</b>${PEOPLE.map(x => whoRow(x, `${x.given.split(' ')[0]} · ${x.fund}`)).join('')}</div>
    <p class="p-note" data-claim-note hidden aria-live="polite"></p>`,
  automations: () => top('Automations', 'Running for you, day and night') + `<div class="shot-panel shot-list">${[
    ['Welcome a new donor', 'Email · wait 3 days · thank-you', true], ['Trek sign-up follow-up', 'Email · WhatsApp if no reply', true],
    ['Missed a call', 'WhatsApp back · create a task', true], ['Fundraising nudge', 'When a page falls behind', false],
  ].map(([n, d, on]) => `<div class="shot-row"><span><b>${n}</b><br><small style="color:var(--color-ink-3)">${d}</small></span><button type="button" class="chiplet green" data-switch aria-pressed="${on}">${on ? '● On' : '○ Off'}</button></div>`).join('')}</div>`,
  tasks: () => top('Tasks', '3 due today') + `<div class="shot-panel shot-list">${[
    'Call Yusuf back about his monthly gift', 'Post Omar his Zakat receipt', 'Check Amina’s training walk date', 'Send the trustees the March report',
  ].map((t, i) => `<label class="shot-task"><input type="checkbox"${i === 3 ? ' checked' : ''}><span>${t}</span></label>`).join('')}</div>`,
  reports: () => top('Reports', 'Where this year’s giving came from') + `<div class="shot-panel shot-list">
    <div class="shot-row"><b>Giving this year</b><span class="shot-seg"><button type="button" data-split="fund" aria-pressed="true">By fund</button><button type="button" data-split="channel" aria-pressed="false">By channel</button></span></div>
    <div class="shot-list" data-hbars></div></div>`,
};
const SPLITS: Record<string, [string, number, string][]> = {
  fund: [['Zakat', 82, '£21,400'], ['Sadaqah', 54, '£14,050'], ['Winter appeal', 40, '£10,380'], ['Atlas Trek 2027', 28, '£7,290']],
  channel: [['Online donation pages', 76, '£19,800'], ['Monthly Direct Debit', 60, '£15,600'], ['Events and treks', 34, '£8,840'], ['Bank and cash', 26, '£6,760']],
};

document.querySelectorAll<HTMLElement>('[data-demo="dashboard"]').forEach(shot => {
  const main = shot.querySelector<HTMLElement>('[data-shot-main]')!;
  const home = main.innerHTML;
  const nav = [...shot.querySelectorAll<HTMLButtonElement>('[data-view]')];
  let added = 0;
  const note = (text: string) => {
    let n = main.querySelector<HTMLElement>('[data-shot-note]');
    if (!n) { n = document.createElement('p'); n.className = 'p-note'; n.dataset.shotNote = ''; n.setAttribute('aria-live', 'polite'); main.append(n); }
    n.textContent = text;
  };
  const bars = (key: string) => {
    const m = METRICS[key]!;
    const title = main.querySelector<HTMLElement>('[data-chart-title]');
    if (title) { title.dataset.home ??= title.innerHTML; title.innerHTML = key === 'gifts' ? title.dataset.home : m.title; }
    main.querySelectorAll<HTMLElement>('[data-bars] i').forEach((b, i) => { b.style.animation = 'none'; b.style.height = `${m.bars[i]}%`; });
  };
  const hbars = (key: string) => {
    const box = main.querySelector<HTMLElement>('[data-hbars]');
    if (box) box.innerHTML = SPLITS[key]!.map(([n, w, v]) => `<div class="shot-hbar"><span class="shot-row"><small>${n}</small><b>${v}</b></span><span class="meter"><i style="width:${w}%"></i></span></div>`).join('');
  };
  const show = (view: string, open?: string) => {
    nav.forEach(b => { const on = b.dataset.view === view; b.classList.toggle('on', on); b.setAttribute('aria-pressed', String(on)); });
    main.innerHTML = view === 'home' ? home : VIEWS[view]!(open);
    if (view === 'home' && added) main.querySelector('[data-supporters]')!.textContent = String(37 + added);
    if (view === 'inbox') {
      const p = person(open ?? 'amina');
      const chatBox = main.querySelector<HTMLElement>('[data-chat]')!;
      p.thread.forEach(([d, t]) => bubble(chatBox.querySelector('[data-thread]')!, d, t));
      main.querySelector(`[data-open="${p.id}"]`)?.classList.add('on');
      chat(chatBox);
      if (open) chatBox.querySelector('input')!.focus({ preventScroll: true });
    }
    if (view === 'reports') hbars('fund');
  };
  nav.forEach(b => b.addEventListener('click', () => show(b.dataset.view!)));
  main.addEventListener('click', e => {
    const t = (e.target as HTMLElement).closest<HTMLElement>('button, input');
    if (!t || !main.contains(t)) return;
    if (t.dataset.metric) {
      main.querySelectorAll<HTMLElement>('[data-metric]').forEach(m => { const on = m === t; m.classList.toggle('on', on); m.setAttribute('aria-pressed', String(on)); });
      bars(t.dataset.metric);
    } else if (t.dataset.open) show('inbox', t.dataset.open);
    else if (t.dataset.profile) show('supporters', t.dataset.profile);
    else if ('add' in t.dataset) {
      added++;
      const count = main.querySelector('[data-supporters]');
      if (count) count.textContent = String(37 + added);
      note(`Supporter added. Their welcome email is on its way, and they are on the “New supporters” list.`);
    } else if ('buildClaim' in t.dataset) {
      const n = main.querySelector<HTMLElement>('[data-claim-note]')!;
      n.hidden = false;
      n.textContent = 'Claim built: 41 gifts, £1,205 of Gift Aid, every one matched to a declaration. Ready to download for HMRC.';
    } else if ('switch' in t.dataset) {
      const on = t.getAttribute('aria-pressed') !== 'true';
      t.setAttribute('aria-pressed', String(on));
      t.textContent = on ? '● On' : '○ Off';
    } else if (t.dataset.split) {
      main.querySelectorAll<HTMLElement>('[data-split]').forEach(x => x.setAttribute('aria-pressed', String(x === t)));
      hbars(t.dataset.split);
    }
  });
});

// ---------- Gift Aid claim: chase the missing declaration ----------
document.querySelectorAll<HTMLElement>('[data-demo="giftaid"]').forEach(root => {
  const noteBox = root.querySelector<HTMLElement>('[data-note]')!;
  const missing = root.querySelector<HTMLElement>('[data-missing]')!;
  let covered = false;
  root.addEventListener('click', async e => {
    const t = (e.target as HTMLElement).closest<HTMLElement>('button');
    if (!t) return;
    if ('sendForm' in t.dataset) {
      noteBox.textContent = 'Declaration form sent to Omar on WhatsApp.';
      missing.className = 'chiplet sun';
      missing.textContent = '… Form sent';
      await wait(1600);
      covered = true;
      missing.className = 'chiplet green';
      missing.textContent = '✓ Covered';
      noteBox.textContent = 'Omar filled in the form. Every gift is covered: £48.75 of Gift Aid to claim.';
    } else if ('claim' in t.dataset) {
      noteBox.textContent = covered
        ? 'Claim ready: 3 gifts, £48.75 of Gift Aid, in HMRC’s schedule for Charities Online.'
        : 'Claim ready for 2 gifts, £18.75. Omar’s gift waits until his declaration comes back.';
    }
  });
});

// ---------- Automation: switch it off and on, test it, choose the branch ----------
document.querySelectorAll<HTMLElement>('[data-demo="flow"]').forEach(root => {
  const sw = root.querySelector<HTMLButtonElement>('[data-switch]')!;
  const branch = root.querySelector<HTMLButtonElement>('[data-branch]')!;
  const title = root.querySelector<HTMLElement>('[data-last-title]')!, last = root.querySelector<HTMLElement>('[data-last-note]')!;
  const was = [title.innerHTML, last.innerHTML];
  const run = root.querySelector<HTMLButtonElement>('[data-run]')!;
  sw.addEventListener('click', () => {
    const on = sw.getAttribute('aria-pressed') !== 'true';
    sw.setAttribute('aria-pressed', String(on));
    sw.textContent = on ? '● On' : '○ Off';
    root.classList.toggle('off', !on);
    run.disabled = !on;
  });
  branch.addEventListener('click', () => {
    const replied = branch.getAttribute('aria-pressed') !== 'true';
    branch.setAttribute('aria-pressed', String(replied));
    [title.innerHTML, last.innerHTML] = replied ? ['Mark as keen and add to Trek 2027', 'They replied, so no nudge needed'] : was;
  });
  run.addEventListener('click', async () => {
    run.disabled = true;
    const label = run.textContent;
    const steps = [...root.querySelectorAll<HTMLElement>('.flow-step')];
    for (const s of steps) { s.classList.add('live'); run.textContent = 'Testing…'; await wait(650); s.classList.remove('live'); }
    run.textContent = 'Test passed: 3 messages, none sent twice';
    await wait(2200);
    run.textContent = label;
    run.disabled = false;
  });
});

// ---------- Donation form: one-off or monthly, amount, Gift Aid ----------
document.querySelectorAll<HTMLFormElement>('[data-demo="give"]').forEach(form => {
  const cta = form.querySelector<HTMLButtonElement>('[data-give]')!;
  const aid = form.querySelector<HTMLButtonElement>('[data-aid]')!;
  const aidTotal = form.querySelector<HTMLElement>('[data-aid-total]')!;
  const done = form.querySelector<HTMLElement>('[data-give-done]')!;
  let freq = 'monthly', amount = 25;
  const pick = (sel: string, b: HTMLElement) => form.querySelectorAll(sel).forEach(x => x.setAttribute('aria-pressed', String(x === b)));
  const render = () => {
    cta.textContent = `Give £${amount}${freq === 'monthly' ? ' a month' : ''}`;
    aidTotal.textContent = aid.getAttribute('aria-pressed') === 'true' ? `(£${(amount * 1.25).toFixed(2).replace('.00', '')} with Gift Aid)` : '';
    done.hidden = true;
  };
  form.querySelectorAll<HTMLButtonElement>('[data-freq]').forEach(b => b.addEventListener('click', () => { freq = b.dataset.freq!; pick('[data-freq]', b); render(); }));
  form.querySelectorAll<HTMLButtonElement>('[data-amount]').forEach(b => b.addEventListener('click', () => { amount = Number(b.dataset.amount); pick('[data-amount]', b); render(); }));
  aid.addEventListener('click', () => { aid.setAttribute('aria-pressed', String(aid.getAttribute('aria-pressed') !== 'true')); render(); });
  form.addEventListener('submit', e => {
    e.preventDefault();
    done.hidden = false;
    done.textContent = `Thank you. £${amount}${freq === 'monthly' ? ' a month' : ''} through Stripe, your thank-you email is on its way${aid.getAttribute('aria-pressed') === 'true' ? ', and Gift Aid is recorded' : ''}.`;
  });
  render();
});

// ---------- Recipes: add one to your automations ----------
document.querySelectorAll<HTMLButtonElement>('[data-recipe]').forEach(b => b.addEventListener('click', () => {
  const on = b.getAttribute('aria-pressed') !== 'true';
  b.setAttribute('aria-pressed', String(on));
  b.querySelector<HTMLElement>('[data-added]')!.hidden = !on;
}));

// ---------- Supporter filters: click a filter on or off, add another ----------
document.querySelectorAll<HTMLElement>('[data-demo="filters"]').forEach(root => {
  const chips = [...root.querySelectorAll<HTMLButtonElement>('[data-filter]')];
  const rows = [...root.querySelectorAll<HTMLTableRowElement>('[data-rows] tr:not([data-empty])')];
  const empty = root.querySelector<HTMLElement>('[data-empty]')!;
  const add = root.querySelector<HTMLButtonElement>('[data-filter-add]')!;
  const apply = () => {
    const on = chips.filter(c => !c.hidden && c.getAttribute('aria-pressed') === 'true');
    let shown = 0;
    rows.forEach(r => { r.hidden = !on.every(c => r.dataset[c.dataset.filter!] === c.dataset.value); if (!r.hidden) shown++; });
    empty.hidden = shown > 0;
  };
  chips.forEach(c => c.addEventListener('click', () => { c.setAttribute('aria-pressed', String(c.getAttribute('aria-pressed') !== 'true')); apply(); }));
  add.addEventListener('click', () => {
    const next = chips.find(c => c.hidden);
    if (next) { next.hidden = false; next.setAttribute('aria-pressed', 'true'); next.focus(); apply(); }
    add.hidden = !chips.some(c => c.hidden);
  });
  apply();
});

// ---------- Fundraising: nudge someone who is behind ----------
document.querySelectorAll<HTMLButtonElement>('[data-demo="nudge"] [data-nudge]').forEach(b => b.addEventListener('click', async () => {
  const card = b.closest<HTMLElement>('.prog')!;
  const pct = card.querySelector<HTMLElement>('[data-pct]')!;
  const bar = card.querySelector<HTMLElement>('.meter > i')!;
  b.disabled = true;
  b.textContent = 'Nudge sent on WhatsApp';
  await wait(1400);
  const now = Number(pct.dataset.pct) + 9;
  pct.dataset.pct = String(now);
  pct.textContent = `${now}% · a gift came in`;
  bar.style.width = `${now}%`;
}));

export {};
